import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from './firebaseConfig';
import { ensureResponderAuth } from './ensureResponderAuth';

function extensionForMimeType(mimeType) {
  if (!mimeType) return 'webm';
  if (mimeType.includes('mp4') || mimeType.includes('m4a') || mimeType.includes('aac')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('caf')) return 'caf';
  return 'webm';
}

function normalizeContentType(mimeType) {
  if (!mimeType) return 'audio/webm';
  const base = mimeType.split(';')[0].trim().toLowerCase();
  if (base === 'audio/x-m4a' || base === 'audio/aac') return 'audio/mp4';
  // Alguns celulares gravam áudio como video/webm ou video/mp4.
  if (base === 'video/webm') return 'audio/webm';
  if (base === 'video/mp4') return 'audio/mp4';
  if (!base.startsWith('audio/')) return 'audio/webm';
  return base;
}

function uploadWithResumable(storageRef, file, contentType) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType });

    task.on(
      'state_changed',
      null,
      (error) => reject(error),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
}

export function formatUploadError(error) {
  if (!error) {
    return 'Erro desconhecido ao enviar áudio.';
  }

  if (error.message && !error.code) {
    return error.message;
  }

  const code = error.code || '';

  if (code === 'auth/operation-not-allowed' || code === 'auth/admin-restricted-operation') {
    return 'Login anônimo desativado. Em Authentication → Sign-in method, habilite o provedor Anonymous.';
  }
  if (code === 'auth/unauthorized-domain') {
    return 'Este domínio não está autorizado no Firebase. Adicione-o em Authentication → Settings → Authorized domains.';
  }
  if (code === 'permission-denied') {
    return 'Permissão negada no Firestore ao salvar o áudio. Publique firestore.rules no Console.';
  }
  if (code === 'storage/unauthorized') {
    return 'Permissão negada no Storage. Publique as regras de storage.rules e habilite Login Anônimo no Firebase.';
  }
  if (code === 'storage/unauthenticated') {
    return 'Sessão não autenticada. Habilite o provedor "Anônimo" em Authentication no Firebase Console.';
  }
  if (code === 'storage/canceled') {
    return 'Envio do áudio cancelado. Tente novamente com conexão estável.';
  }
  if (code === 'storage/retry-limit-exceeded') {
    return 'Conexão instável. O áudio não foi enviado; tente novamente com Wi-Fi.';
  }

  return `Não foi possível enviar o áudio (${code || 'sem código'}). Verifique conexão e permissões do Firebase Storage.`;
}

export async function uploadInterviewAudio(questionarioId, resultId, blob, mimeType) {
  if (!blob || blob.size === 0) {
    throw new Error('Gravação vazia — nenhum áudio capturado pelo dispositivo.');
  }

  await ensureResponderAuth();

  const contentType = normalizeContentType(mimeType || blob.type);
  const ext = extensionForMimeType(contentType);
  const file = new File([blob], `${resultId}.${ext}`, { type: contentType });
  const storageRef = ref(storage, `recordings/${questionarioId}/${resultId}.${ext}`);

  const maxAttempts = 3;
  let lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await uploadWithResumable(storageRef, file, contentType);
    } catch (error) {
      lastError = error;
      console.error(`Tentativa ${attempt} de upload de áudio falhou:`, error);
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }
    }
  }

  throw lastError;
}
